import React, { useEffect, useId, useMemo, useRef, useState } from "react";

// Draws a lesson diagram described by the backend (see backend/agents/diagrams.py): shapes on a W x H canvas plus
// labels that point at parts of it. Labels are laid out here, so they never overlap and are always spelled correctly.
const GAP = 6.5;       // minimum vertical distance between two labels
const MARGIN = 46;     // room on each side of the canvas for labels

function spread(items, top, bottom) {
  // keep labels at least GAP apart, as close to their own anchor as possible
  const sorted = [...items].sort((a, b) => a.y - b.y).map((l) => ({ ...l, ly: l.y }));
  for (let i = 1; i < sorted.length; i++) sorted[i].ly = Math.max(sorted[i].ly, sorted[i - 1].ly + GAP);
  const over = sorted.length ? sorted[sorted.length - 1].ly - bottom : 0;
  if (over > 0) {
    for (let i = sorted.length - 1; i >= 0; i--) {
      sorted[i].ly -= over;
      if (i < sorted.length - 1) sorted[i].ly = Math.min(sorted[i].ly, sorted[i + 1].ly - GAP);
    }
  }
  if (sorted[0] && sorted[0].ly < top) {
    const shift = top - sorted[0].ly;
    sorted.forEach((l) => { l.ly += shift; });
  }
  return sorted;
}

function Shape({ s, arrowId }) {
  const common = {
    fill: s.fill, stroke: s.stroke, strokeWidth: s.stroke_width,
    strokeDasharray: s.dash ? "2 1.5" : undefined, strokeLinejoin: "round", strokeLinecap: "round",
  };
  switch (s.type) {
    case "circle": return <circle cx={s.cx} cy={s.cy} r={s.r} {...common} />;
    case "ellipse": return <ellipse cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} {...common} />;
    case "rect": return <rect x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx} {...common} />;
    case "line": return <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} {...common} markerEnd={s.arrow ? `url(#${arrowId})` : undefined} />;
    case "polygon": return <polygon points={s.points.map((p) => p.join(",")).join(" ")} {...common} />;
    case "polyline": return <polyline points={s.points.map((p) => p.join(",")).join(" ")} {...common} />;
    case "path": return <path d={s.d} {...common} markerEnd={s.arrow ? `url(#${arrowId})` : undefined} />;
    case "text": return <text x={s.x} y={s.y} fontSize={s.size} fill={s.fill} textAnchor="middle" className="cs-dg-text">{s.text}</text>;
    default: return null;
  }
}

export default function Diagram({ diagram }) {
  const uid = useId().replace(/:/g, "");
  const wrap = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const [quiz, setQuiz] = useState(false);
  const [shown, setShown] = useState(() => new Set());

  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const check = () => setNarrow(el.clientWidth < 540);
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { setShown(new Set()); setQuiz(false); }, [diagram]);

  const view = diagram.view || { x: 0, y: 0, w: diagram.width, h: diagram.height };
  // a label with no letters ("0", "10") is an axis number that older diagrams stored as a part: never list those
  const labels = (diagram.labels || []).filter((l) => /[A-Za-z]/.test(l.text || ""));
  const layout = useMemo(() => {
    // numbered markers are nudged apart when two parts sit very close, so every number stays readable
    const numbered = labels.map((l, i) => ({ ...l, n: i + 1, mx: l.x, my: l.y }));
    for (let pass = 0; pass < 12; pass++) {
      for (let i = 0; i < numbered.length; i++) {
        for (let j = i + 1; j < numbered.length; j++) {
          const a = numbered[i];
          const b = numbered[j];
          let dx = b.mx - a.mx;
          let dy = b.my - a.my;
          const d = Math.hypot(dx, dy) || 0.01;
          if (d < 6.6) {
            if (d < 0.05) { dx = 1; dy = 0; }
            const push = (6.6 - d) / 2;
            const ux = dx / d;
            const uy = dy / d;
            a.mx -= ux * push; a.my -= uy * push;
            b.mx += ux * push; b.my += uy * push;
          }
        }
      }
    }
    const mid = view.x + view.w / 2;
    const left = spread(numbered.filter((l) => l.x < mid), view.y + 2, view.y + view.h - 2);
    const right = spread(numbered.filter((l) => l.x >= mid), view.y + 2, view.y + view.h - 2);
    return { numbered, left, right };
  }, [labels, view.x, view.y, view.w, view.h]);

  const reveal = (n) => setShown((prev) => new Set(prev).add(n));
  const hidden = (n) => quiz && !shown.has(n);
  const toggleQuiz = () => { setQuiz((q) => !q); setShown(new Set()); };

  // each side gets as much room as its longest label needs (about 2 units per letter at this font size)
  const need = (side) => Math.max(0, ...side.map((l) => l.text.length)) * 2.05 + 6;
  const marginL = narrow ? 4 : Math.max(MARGIN, need(layout.left));
  const marginR = narrow ? 4 : Math.max(MARGIN, need(layout.right));
  const viewBox = `${view.x - marginL} ${view.y} ${view.w + marginL + marginR} ${view.h}`;
  const arrowId = `arrow-${uid}`;

  const renderLabel = (l, side) => {
    const x = side === "left" ? view.x - 3 : view.x + view.w + 3;
    const anchor = side === "left" ? "end" : "start";
    const text = hidden(l.n) ? "?" : l.text;
    return (
      <g
        key={l.n}
        className={`cs-dg-label ${quiz ? "quiz" : ""}`}
        onClick={quiz ? () => reveal(l.n) : undefined}
        role={quiz ? "button" : undefined}
        tabIndex={quiz ? 0 : undefined}
        aria-label={quiz && hidden(l.n) ? "Hidden label. Press to reveal" : l.text}
        onKeyDown={quiz ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); reveal(l.n); } } : undefined}
      >
        <line x1={l.x} y1={l.y} x2={x} y2={l.ly} className="cs-dg-leader" />
        <circle cx={l.x} cy={l.y} r="1" className="cs-dg-dot" />
        <text x={side === "left" ? x - 1 : x + 1} y={l.ly + 1.3} textAnchor={anchor} fontSize="3.6" className="cs-dg-labeltext">{text}</text>
      </g>
    );
  };

  return (
    <figure className="cs-diagram" ref={wrap}>
      <div className="cs-dg-head">
        <figcaption className="cs-dg-title">{diagram.title}</figcaption>
        {labels.length > 0 && (
          <button type="button" className={`cs-chip ${quiz ? "on" : ""}`} onClick={toggleQuiz} aria-pressed={quiz}>
            {quiz ? "Show all labels" : "Test yourself"}
          </button>
        )}
      </div>
      <svg viewBox={viewBox} className="cs-dg-svg" role="img" aria-label={`${diagram.title}. Labelled parts: ${labels.map((l) => l.text).join(", ")}`}>
        <defs>
          <marker id={arrowId} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="3.4" markerHeight="3.4" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill="#374151" />
          </marker>
        </defs>
        {diagram.shapes.map((s, i) => <Shape key={i} s={s} arrowId={arrowId} />)}
        {narrow
          ? layout.numbered.map((l) => (
            <g key={l.n} className="cs-dg-num">
              <circle cx={l.mx} cy={l.my} r="3" />
              <text x={l.mx} y={l.my + 1.3} textAnchor="middle" fontSize="3.6">{l.n}</text>
            </g>
          ))
          : (
            <>
              {layout.left.map((l) => renderLabel(l, "left"))}
              {layout.right.map((l) => renderLabel(l, "right"))}
            </>
          )}
      </svg>
      {narrow && labels.length > 0 && (
        <ol className="cs-dg-legend">
          {layout.numbered.map((l) => (
            <li key={l.n}>
              {hidden(l.n)
                ? <button type="button" className="cs-dg-reveal" onClick={() => reveal(l.n)}>Tap to reveal</button>
                : l.text}
            </li>
          ))}
        </ol>
      )}
      {diagram.caption && <p className="cs-dg-caption">{diagram.caption}</p>}
    </figure>
  );
}
